import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Camera, Copy, Loader2, MessageSquare, Send, X } from 'lucide-react';
import { submitFeedback, getUploadUrl } from '../lib/backOfficeApi';
import { toast } from '../lib/buses';

const CATEGORIES = [
  { id: 'bug', label: 'Bug' },
  { id: 'ux', label: 'UX issue' },
  { id: 'feature', label: 'Feature request' },
  { id: 'data', label: 'Data problem' },
  { id: 'other', label: 'Other' },
];

export const FeedbackModal = ({ open, onClose }) => {
  const [step, setStep] = useState('capture'); // capture | review | sending
  const [screenshot, setScreenshot] = useState(null);
  const [comment, setComment] = useState('');
  const [category, setCategory] = useState('bug');
  const canvasRef = useRef(null);

  const capture = useCallback(async () => {
    setStep('capture');
    try {
      const html2canvas = (await import('html2canvas')).default;
      // Hide the modal backdrop before capturing
      const backdrop = document.querySelector('[data-feedback-backdrop]');
      if (backdrop) backdrop.style.display = 'none';
      const canvas = await html2canvas(document.body, {
        useCORS: true,
        allowTaint: true,
        scale: 1,
        logging: false,
        backgroundColor: '#F7F6F2',
        // Exclude the feedback modal itself from the screenshot
        ignoreElements: (el) => el.hasAttribute?.('data-feedback-modal'),
      });
      if (backdrop) backdrop.style.display = '';
      const dataUrl = canvas.toDataURL('image/png');
      setScreenshot(dataUrl);
      setStep('review');
    } catch (e) {
      toast('Screenshot capture failed', 'error', e?.message);
      setStep('review');
    }
  }, []);

  // Auto-capture on open
  useEffect(() => {
    if (open) {
      setScreenshot(null);
      setComment('');
      setCategory('bug');
      // Small delay so the modal renders and we can exclude it
      const t = setTimeout(capture, 200);
      return () => clearTimeout(t);
    }
  }, [open, capture]);

  const retake = () => { setScreenshot(null); capture(); };

  const submit = async () => {
    if (!comment.trim()) { toast('Add a comment describing the issue', 'error'); return; }
    setStep('sending');
    try {
      let screenshotUrl;

      // Upload screenshot to R2 via the existing presign flow
      if (screenshot) {
        const blob = await (await fetch(screenshot)).blob();
        const file = new File([blob], `feedback-${Date.now()}.png`, { type: 'image/png' });
        const presign = await getUploadUrl(file);
        if (presign?.uploadUrl && presign?.publicUrl) {
          await fetch(presign.uploadUrl, {
            method: 'PUT',
            headers: { 'Content-Type': 'image/png' },
            body: file,
          });
          screenshotUrl = presign.publicUrl;
        }
      }

      await submitFeedback({
        message: comment.trim(),
        screenshotUrl,
        category,
        pageUrl: window.location.href,
        metadata: { url: window.location.href, timestamp: new Date().toISOString() },
      });
      toast('Feedback sent', 'success', 'Thank you — your feedback has been recorded');
      onClose();
    } catch (e) {
      toast('Failed to send feedback', 'error', e?.message);
      setStep('review');
    }
  };

  if (!open) return null;

  return (
    <div data-feedback-backdrop className="fixed inset-0 z-[9999] bg-[#1F2937]/50 flex items-center justify-center p-4">
      <div data-feedback-modal className="bg-white rounded-[12px] shadow-xl w-full max-w-[560px] max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-5 pt-5 pb-3 border-b border-[#EEEDEA] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-[#1F2937]" />
            <span className="text-[14.5px] font-semibold text-[#1F2937]">Send feedback</span>
          </div>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded hover:bg-[#F2F1ED]">
            <X className="w-4 h-4 text-[#6B7280]" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Screenshot preview */}
          <div>
            <div className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-[#6B7280] mb-1.5">Screenshot</div>
            {step === 'capture' ? (
              <div className="border border-[#E6E4E1] rounded-[10px] bg-[#FBFAF7] h-[200px] flex items-center justify-center">
                <div className="text-center">
                  <Camera className="w-6 h-6 text-[#9CA3AF] mx-auto mb-2 animate-pulse" />
                  <div className="text-[11.5px] text-[#6B7280]">Capturing page…</div>
                </div>
              </div>
            ) : screenshot ? (
              <div className="border border-[#E6E4E1] rounded-[10px] overflow-hidden relative group">
                <img src={screenshot} alt="Page screenshot" className="w-full max-h-[200px] object-cover object-top" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                  <button onClick={retake} className="opacity-0 group-hover:opacity-100 transition-opacity h-8 px-3 rounded-md text-[12px] font-medium bg-white text-[#1F2937] shadow">Retake screenshot</button>
                </div>
              </div>
            ) : (
              <div className="border border-[#F1D2D2] rounded-[10px] bg-[#FBE9E9] h-[120px] flex items-center justify-center">
                <div className="text-center">
                  <div className="text-[12px] text-[#9B1F1F] mb-2">Screenshot capture failed</div>
                  <button onClick={retake} className="text-[11.5px] text-[#1E40AF] hover:underline">Retry</button>
                </div>
              </div>
            )}
          </div>

          {/* Category */}
          <div>
            <div className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-[#6B7280] mb-1.5">Category</div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {CATEGORIES.map(c => (
                <button
                  key={c.id}
                  onClick={() => setCategory(c.id)}
                  className={`px-2.5 py-1 rounded-md text-[11.5px] font-medium border transition-colors ${
                    category === c.id ? 'border-[#1F2937] bg-[#1F2937] text-white' : 'border-[#E6E4E1] text-[#6B7280] hover:border-[#D8D5CF]'
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          {/* Comment */}
          <div>
            <div className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-[#6B7280] mb-1.5">Comment <span className="text-[#9B1F1F]">*</span></div>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Describe what you see, what you expected, or what you'd like…"
              rows={4}
              className="w-full bg-white border border-[#E6E4E1] rounded-md px-3 py-2 text-[12.5px] placeholder:text-[#9CA3AF] focus:outline-none focus:border-[#1F2937] resize-none"
            />
          </div>

          <div className="bg-[#FBFAF7] border border-[#E6E4E1] rounded-md px-3 py-2 text-[11px] text-[#6B7280]">
            Your feedback includes the current page URL and a screenshot. Saved to the feedback queue for review.
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-[#EEEDEA] bg-[#FBFAF7] flex items-center justify-between">
          <button onClick={onClose} className="h-8 px-3 rounded-md text-[12px] font-medium text-[#1F2937] hover:bg-[#F2F1ED]">Cancel</button>
          <button
            onClick={submit}
            disabled={step === 'capture' || step === 'sending' || !comment.trim()}
            className="h-8 px-3 rounded-md text-[12px] font-medium bg-[#1F2937] hover:bg-[#111827] disabled:bg-[#C5C2BC] text-white inline-flex items-center gap-1.5"
          >
            {step === 'sending' ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />Sending…</> : <><Send className="w-3.5 h-3.5" />Send feedback</>}
          </button>
        </div>
      </div>
    </div>
  );
};
